import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Briefcase,
  Scales,
  Users,
  CurrencyGbp,
  NoteBlank,
  Plus,
  CircleNotch,
  WarningCircle,
} from '@phosphor-icons/react';
import { useMattersStore } from '@/lib/mattersStore';
import { useAuthStore } from '@/features/auth/store';
import { canCreateMatter } from '@/lib/pupilUiBoundary';
import { EmptyState } from '@/components/shared/EmptyState';
import { useChambersMode } from '@/lib/useChambersMode';
import * as conflictsApi from '@/lib/conflictsApi';
import { linkEmailToMatter, linkConversationToMatter } from '@/lib/inboxApi';
import type { ConflictScanResult } from '@/lib/conflictsApi';
import { fetchCourts, type Court } from '@/lib/courtsApi';
import { fetchPracticeAreas, type PracticeArea } from '@/lib/practiceAreasApi';
import { fetchBarristers, type ApiBarrister } from '@/lib/barristersApi';
import {
  isValidPhone as isValidPhoneShared,
  isValidEmail as isValidEmailShared,
  // Matter name uses the UK legal matter-name rule (mirrors the API).
  isValidMatterName,
  sanitizeMatterName,
  MATTER_NAME_ERROR,
  // Party and solicitor names are entity names — a firm is as likely as a person.
  isValidEntityName,
  ENTITY_NAME_ERROR,
  PHONE_ERROR,
  EMAIL_ERROR,
} from '@/lib/validators';
import { phoneLengthError } from '@/lib/phone-rules';
import { sanitizeMoneyInput, parseMoney, formatMoney2 } from '@/lib/money';
import { PhoneInput } from '@/components/ui/phone-input';
import { ExpandableTextarea } from '@/components/ui/expandable-textarea';
import { PageHeader } from '@/components/shared/PageHeader';
import { ContactPicker, type PickedContact } from '@/components/shared/ContactPicker';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

// ─── Static enums (these map directly to API-accepted values) ─

const CASE_TYPES: { label: string; value: string }[] = [
  { label: 'Civil',                value: 'civil' },
  { label: 'Criminal',             value: 'criminal' },
  { label: 'Family',               value: 'family' },
  { label: 'Commercial',           value: 'commercial' },
  { label: 'Chancery',             value: 'chancery' },
  { label: 'Employment',           value: 'employment' },
  { label: 'Public Law',           value: 'public_law' },
  { label: 'Immigration',          value: 'immigration' },
  { label: 'Tax',                  value: 'tax' },
  { label: 'Property',             value: 'property' },
  { label: 'Personal Injury',      value: 'personal_injury' },
  { label: 'Clinical Negligence',  value: 'clinical_negligence' },
  { label: 'Inquest',              value: 'inquest' },
  { label: 'Arbitration',          value: 'arbitration' },
  { label: 'Other',                value: 'other' },
];

const FEE_TYPES: { label: string; value: string }[] = [
  { label: 'Brief fee',  value: 'brief_fee' },
  { label: 'Refresher',  value: 'refresher' },
  { label: 'Hourly',     value: 'hourly' },
  { label: 'Fixed',      value: 'fixed' },
  { label: 'Conference', value: 'conference' },
  { label: 'Opinion',    value: 'opinion' },
  { label: 'CFA',        value: 'cfa' },
  { label: 'DBA',        value: 'dba' },
  { label: 'Pro bono',   value: 'pro_bono' },
];


const PRIORITIES: { label: string; value: string }[] = [
  { label: 'Low',    value: 'low' },
  { label: 'Normal', value: 'normal' },
  { label: 'High',   value: 'high' },
  { label: 'Urgent', value: 'urgent' },
];

// ─── Field limits — MUST match apps/api/src/modules/matters/matters.schema.ts ──
//
// The API is the gate; these exist so the user is told at the keyboard instead of
// by a 400 after they press Create.

/** `description` / `internalNotes` are capped at 10 000 on the API. */
const TEXT_MAX = 10_000;
/** `courtCaseNumber` is capped at 100 on the API. */
const COURT_REF_MAX = 100;
/** Upper bound on a fee estimate. A figure above this is a typo, not an instruction. */
const FEE_ESTIMATE_MAX = 10_000_000;

// ─── Shared input styles ──────────────────────────────────────

const inputCls =
  'h-9 w-full rounded-md border border-border bg-card px-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand-600/40';
const selectCls = `${inputCls} appearance-none`;
const labelCls = 'block text-xs font-medium text-muted-foreground mb-1.5';

const emptyContact: PickedContact = { name: '', contactId: undefined };

// ─── Component ───────────────────────────────────────────────

// Type for state passed from intake conversion
interface FromIntakeState {
  fromIntake?: {
    name?: string;
    practiceArea?: string;
    caseType?: string;
    court?: string;
    summary?: string;
    partyAName?: string;
    partyAType?: string;
    solicitorFirmName?: string;
    solicitorName?: string;
    solicitorEmail?: string;
    opposingPartyName?: string;
  };
  // Set when creating a matter straight from an inbox instruction — the email/conversation is
  // auto-linked to the new matter on success, so triage is one fluid action (no round-trip).
  linkEmailId?: string;
  linkConversationId?: string;
}

export default function NewMatterPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const chambersMode = useChambersMode();
  const isClerk      = user?.memberType === 'senior_clerk' || user?.memberType === 'junior_clerk';
  const isBarrister  = ['silk', 'junior', 'pupil', 'door_tenant'].includes(user?.memberType ?? '');
  const isSolo       = chambersMode.isSolo && isBarrister;
  const basePath     = isClerk ? '/clerk' : isBarrister ? '/barrister' : '/admin';
  const addMatter    = useMattersStore((s) => s.addMatter);

  // Check if we came from intake conversion
  const locationState = location.state as FromIntakeState | undefined;
  const fromIntake = locationState?.fromIntake;

  // ── Reference data from real APIs ──
  const [courts, setCourts] = useState<Court[]>([]);
  const [practiceAreas, setPracticeAreas] = useState<PracticeArea[]>([]);
  const [refLoading, setRefLoading] = useState(true);
  // Chambers barristers, for the lead-assignment dropdown (admins/clerks only — a barrister
  // creating their own matter is auto-assigned as lead, so they never see this list).
  const [barristers, setBarristers] = useState<ApiBarrister[]>([]);
  const [leadBarristerId, setLeadBarristerId] = useState('');

  useEffect(() => {
    let done = 0;
    const finish = () => { if (++done === 2) setRefLoading(false); };
    fetchCourts()
      .then((c) => setCourts(c.filter((x) => x.isActive)))
      .catch(() => toast.error('Could not load courts'))
      .finally(finish);
    fetchPracticeAreas()
      .then((p) => setPracticeAreas(p.filter((x) => x.isActive)))
      .catch(() => toast.error('Could not load practice areas'))
      .finally(finish);
  }, []);

  // Load assignable barristers only when the creator can assign one (admin/clerk).
  useEffect(() => {
    if (isBarrister) return;
    fetchBarristers()
      .then((list) => setBarristers(list.filter((b) => b.user.status === 'active' || b.user.status === 'invited')))
      .catch(() => { /* non-fatal: the field just stays empty and assignment can be done later via a brief */ });
  }, [isBarrister]);

  // ── Form state ── (pre-fill from intake if available)
  const [name, setName] = useState(fromIntake?.name ?? '');
  const [courtCaseNumber, setCourtCaseNumber] = useState('');
  const [courtId, setCourtId] = useState('');
  const [courtroom, setCourtroom] = useState('');
  const [judge, setJudge] = useState('');
  const [practiceAreaId, setPracticeAreaId] = useState('');
  const [subArea, setSubArea] = useState('');
  const [caseType, setCaseType] = useState(fromIntake?.caseType ?? '');
  const [feeType, setFeeType] = useState('');
  const [feeEstimate, setFeeEstimate] = useState('');
  const [priority, setPriority] = useState('normal');
  const [summary, setSummary] = useState(fromIntake?.summary ?? '');
  const [notes, setNotes] = useState('');
  const [partyARole, setPartyARole] = useState('claimant');
  const [partyBRole, setPartyBRole] = useState('defendant');

  const [partyA, setPartyA] = useState<PickedContact>(
    fromIntake?.partyAName ? { name: fromIntake.partyAName, contactId: undefined } : emptyContact
  );
  const [partyB, setPartyB] = useState<PickedContact>(
    fromIntake?.opposingPartyName ? { name: fromIntake.opposingPartyName, contactId: undefined } : emptyContact
  );
  const [solicitor, setSolicitor] = useState<PickedContact>(
    fromIntake?.solicitorName
      ? { name: `${fromIntake.solicitorName} (${fromIntake.solicitorFirmName})`, contactId: undefined }
      : emptyContact
  );
  // Instructing solicitor contact details (optional). Primary + alternate number + email.
  const [solicitorPhone, setSolicitorPhone] = useState('');
  const [solicitorAltPhone, setSolicitorAltPhone] = useState('');
  const [solicitorEmail, setSolicitorEmail] = useState(fromIntake?.solicitorEmail ?? '');

  const [partyAPhone, setPartyAPhone] = useState('');
  const [partyAAltPhone, setPartyAAltPhone] = useState('');
  const [partyAEmail, setPartyAEmail] = useState('');
  const [partyBPhone, setPartyBPhone] = useState('');
  const [partyBEmail, setPartyBEmail] = useState('');

  // ── Progressive disclosure ──
  // The form ran to two full screens, most of it fields that are usually left blank. The
  // rarely-used ones now sit behind a toggle. Anything holding a value or an error forces
  // its section open (see `moreOpen` / `descOpen` below) — a hidden error would block the
  // Create button with nothing on screen to explain why.
  const [showMore, setShowMore] = useState(false);
  const [showDescription, setShowDescription] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const [submitted, setSubmitted] = useState(false);
  const markTouched = (k: string) => setTouched((p) => new Set(p).add(k));

  // ── Live conflict scan ──
  const [scanResults, setScanResults] = useState<ConflictScanResult[]>([]);
  const [scanning, setScanning] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Critical-conflict override. The server gate accepts a documented reason (10+ characters) from a
  // silk (KC) or admin only, records it on the audit trail and marks the matter "approved with
  // override". This page had no way to send it, so a critical conflict was a dead end for everyone.
  const canOverrideConflict = user?.memberType === 'silk' || user?.memberType === 'admin';
  const [overrideReason, setOverrideReason] = useState('');
  // The server also blocks conflicts the live scan cannot see (it checks the lead barrister too).
  const [serverConflict, setServerConflict] = useState<string | null>(null);

  const runScan = useCallback(async () => {
    const parties = [
      ...(partyA.name.trim()
        ? [{
            name: partyA.name.trim(),
            role: partyARole,
            contactId: partyA.contactId,
            ...(partyAPhone.trim() ? { phone: partyAPhone.trim() } : {}),
            ...(partyAAltPhone.trim() ? { altPhone: partyAAltPhone.trim() } : {}),
          }]
        : []),
      ...(partyB.name.trim() ? [{
            name: partyB.name.trim(),
            role: partyBRole,
            contactId: partyB.contactId,
            ...(partyBPhone.trim() ? { phone: partyBPhone.trim() } : {}),
          }] : []),
    ];
    if (parties.length === 0) { setScanResults([]); return; }
    setScanning(true);
    try {
      const clerkId = isClerk ? (user?.id ?? undefined) : undefined;
      const result = await conflictsApi.scanForConflicts(parties, undefined, undefined, clerkId);
      setScanResults(result.conflicts);
    } catch {
      // Silent — a failed scan must never block matter creation
    } finally {
      setScanning(false);
    }
  }, [partyA.name, partyA.contactId, partyARole, partyAPhone, partyAAltPhone, partyB.name, partyB.contactId, partyBRole, partyBPhone, isClerk, user?.id]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(runScan, 500);
    // Parties changed — a previous server block no longer describes this matter.
    setServerConflict(null);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [runScan]);

  const hasCritical = scanResults.some((r) => r.severity === 'critical');
  const needsOverride = hasCritical || serverConflict !== null;
  const overrideReady = canOverrideConflict && overrideReason.trim().length >= 10;

  // ── Live field validation (errors shown on blur or after a submit attempt) ──
  const fieldErrors = useMemo<Record<string, string>>(() => {
    const e: Record<string, string> = {};
    if (!name.trim()) e.name = 'Matter name is required.';
    else if (!isValidMatterName(name)) e.name = MATTER_NAME_ERROR;

    // Case type is REQUIRED by the API (caseTypeEnum, not optional). It used to be
    // substituted with 'other' when left blank, which silently mis-filed the matter.
    if (!caseType) e.caseType = 'Case type is required.';

    if (courtCaseNumber.trim().length > COURT_REF_MAX)
      e.courtCaseNumber = `Court case reference cannot exceed ${COURT_REF_MAX} characters.`;

    // Client (party A) — name, phone and email are required; alt phone is optional.
    if (!partyA.name.trim()) e.partyAName = 'Client name is required.';
    else if (!isValidEntityName(partyA.name)) e.partyAName = ENTITY_NAME_ERROR;

    if (!partyAPhone.trim()) e.partyAPhone = 'Client phone is required.';
    else if (!isValidPhoneShared(partyAPhone)) e.partyAPhone = phoneLengthError(partyAPhone) ?? PHONE_ERROR;

    if (partyAAltPhone.trim() && !isValidPhoneShared(partyAAltPhone)) e.partyAAltPhone = phoneLengthError(partyAAltPhone) ?? PHONE_ERROR;

    if (!partyAEmail.trim()) e.partyAEmail = 'Client email is required.';
    else if (!isValidEmailShared(partyAEmail)) e.partyAEmail = EMAIL_ERROR;

    // Opposing party (party B) — optional, but once named it must be valid, and it
    // must not be the same contact as our own client (that would defeat the conflict scan).
    if (partyB.name.trim() && !isValidEntityName(partyB.name)) e.partyBName = ENTITY_NAME_ERROR;
    if (partyA.contactId && partyB.contactId && partyA.contactId === partyB.contactId)
      e.partyBName = 'The opposing party cannot be the same contact as your client.';
    if (partyBPhone.trim() && !isValidPhoneShared(partyBPhone)) e.partyBPhone = phoneLengthError(partyBPhone) ?? PHONE_ERROR;
    if (partyBEmail.trim() && !isValidEmailShared(partyBEmail)) e.partyBEmail = EMAIL_ERROR;

    // Instructing solicitor details are optional — validate format only when supplied.
    // The name is only sent when creating a NEW solicitor (a picked contact keeps its own name).
    if (!solicitor.contactId && solicitor.name.trim() && !isValidEntityName(solicitor.name))
      e.solicitorName = ENTITY_NAME_ERROR;
    if (solicitorPhone.trim() && !isValidPhoneShared(solicitorPhone)) e.solicitorPhone = phoneLengthError(solicitorPhone) ?? PHONE_ERROR;
    if (solicitorAltPhone.trim() && !isValidPhoneShared(solicitorAltPhone)) e.solicitorAltPhone = phoneLengthError(solicitorAltPhone) ?? PHONE_ERROR;
    if (solicitorEmail.trim() && !isValidEmailShared(solicitorEmail)) e.solicitorEmail = EMAIL_ERROR;

    // Fee estimate — `sanitizeMoneyInput` already blocks letters and `formatMoney2` rounds to
    // pence on blur (4.567 → 4.57), so sub-penny precision is corrected rather than refused.
    // What is left to catch is a figure that is out of range.
    if (feeEstimate.trim()) {
      const fee = parseMoney(feeEstimate);
      if (!Number.isFinite(fee)) e.feeEstimate = 'Enter a valid amount.';
      else if (fee < 0) e.feeEstimate = 'A fee estimate cannot be negative.';
      else if (fee > FEE_ESTIMATE_MAX)
        e.feeEstimate = `A fee estimate cannot exceed £${FEE_ESTIMATE_MAX.toLocaleString()}.`;
    }

    if (summary.length > TEXT_MAX) e.summary = `Matter summary cannot exceed ${TEXT_MAX.toLocaleString()} characters.`;
    if (notes.length > TEXT_MAX) e.notes = `Internal notes cannot exceed ${TEXT_MAX.toLocaleString()} characters.`;

    return e;
  }, [
    name, caseType, courtCaseNumber,
    partyA.name, partyA.contactId, partyAPhone, partyAAltPhone, partyAEmail,
    partyB.name, partyB.contactId, partyBPhone, partyBEmail,
    solicitor.name, solicitor.contactId, solicitorPhone, solicitorAltPhone, solicitorEmail,
    feeEstimate, summary, notes,
  ]);

  /** A field's error, but only once it has been touched or a submit was attempted. */
  const errFor = (k: string): string | undefined =>
    submitted || touched.has(k) ? fieldErrors[k] : undefined;

  /** Optional extras stay open once they hold anything, or once they have an error. */
  const moreOpen =
    showMore ||
    !!subArea.trim() || !!courtCaseNumber.trim() || !!partyAAltPhone.trim() || !!solicitorAltPhone.trim() ||
    !!errFor('courtCaseNumber') || !!errFor('partyAAltPhone') || !!errFor('solicitorAltPhone');

  /**
   * How many short fields Basic Information actually draws right now.
   *
   * Practice area and Case type are always there; Sub-area and Court case reference only when
   * "more" is open; Lead barrister only for a clerk or admin, since a barrister opening their
   * own matter is assigned to it automatically. A fixed three columns left an empty gap
   * whenever that count was not a multiple of three — which is the collapsed state everyone
   * sees first: two fields, a third of the row unused.
   */
  const basicFieldCount = 2 + (moreOpen ? 2 : 0) + (isBarrister ? 0 : 1);
  const basicCols = basicFieldCount % 2 === 0 && basicFieldCount % 3 !== 0
    ? 'lg:grid-cols-2'
    : 'lg:grid-cols-3';

  /** Same for the description block, which is often skipped when a matter is opened in a hurry. */
  const descOpen =
    showDescription || !!summary.trim() || !!notes.trim() || !!errFor('summary') || !!errFor('notes');

  const canCreate = (!needsOverride || overrideReady) && !scanning && !submitting;

  // ── Submit ──
  const handleCreate = () => {
    setSubmitted(true);
    if (Object.keys(fieldErrors).length > 0 || (needsOverride && !overrideReady)) {
      const labels: Record<string, string> = {
        name: 'Matter name',
        caseType: 'Case type',
        courtCaseNumber: 'Court case reference',
        partyAName: 'Client name',
        partyAPhone: 'Client phone',
        partyAAltPhone: 'Alt. phone',
        partyAEmail: 'Client email',
        partyBName: 'Opposing party name',
        partyBPhone: 'Opposing party phone',
        partyBEmail: 'Opposing party email',
        solicitorName: 'Instructing solicitor',
        solicitorPhone: 'Solicitor primary number',
        solicitorAltPhone: 'Solicitor alternate number',
        solicitorEmail: 'Solicitor email',
        feeEstimate: 'Fee estimate',
        summary: 'Matter summary',
        notes: 'Internal notes',
      };
      const list = Object.entries(fieldErrors).map(([k, m]) => `${labels[k] ?? k}: ${m}`);
      if (needsOverride && !overrideReady) {
        list.push(canOverrideConflict
          ? 'A critical conflict was detected — record an override reason (at least 10 characters) to proceed.'
          : 'A critical conflict was detected — only a silk (KC) or the chambers admin can override it.');
      }
      setValidationErrors(list);
      const fieldCount = Object.keys(fieldErrors).length;
      if (fieldCount > 0) {
        toast.error(`Please fix ${fieldCount} required field${fieldCount !== 1 ? 's' : ''} — see the highlighted fields below.`);
      }
      return;
    }
    setValidationErrors([]);
    setSubmitting(true);

    // Resolve the lead barrister: a barrister creating their own matter is auto-lead; an
    // admin/clerk may pick one from the dropdown (or leave it unassigned to allocate later).
    const assignedLeadId = isBarrister ? (user?.barristerId ?? undefined) : (leadBarristerId || undefined);
    const assignedBarrister = !isBarrister && leadBarristerId
      ? barristers.find((b) => b.id === leadBarristerId)
      : null;

    // A matter with a lead barrister (self-assigned OR directly assigned) goes straight to Active;
    // an unassigned admin/clerk matter starts in Intake and is activated when a brief is accepted.
    const initialStatus = isBarrister || assignedBarrister ? 'active' : 'intake';
    const barristerDisplay = isBarrister && user
      ? `${user.firstName} ${user.lastName}${user.memberType === 'silk' ? ' KC' : ''}`
      : assignedBarrister?.fullName ?? 'Unassigned';

    addMatter(
      {
        number: '',
        name: name.trim(),
        court: courts.find((c) => c.id === courtId)?.shortName ?? '',
        barrister: barristerDisplay,
        status: initialStatus,
        next: '—',
        area: practiceAreas.find((p) => p.id === practiceAreaId)?.name ?? '',
        subArea,
        courtroom,
        judge,
        feeType,
        feeEstimate: parseMoney(feeEstimate),
        riskRating: priority,
        summary,
        internalNotes: notes,
        role: isBarrister ? 'lead' : '',
      },
      {
        // Validated as required above — never substitute 'other', which silently mis-files the matter.
        caseType,
        courtCaseNumber: courtCaseNumber.trim() || undefined,
        courtId: courtId || undefined,
        practiceAreaId: practiceAreaId || undefined,
        priority,
        // Lead barrister: self (barrister) or the admin/clerk's dropdown pick (may be undefined).
        // The backend publishes MatterAssigned and propagates access to the assignee.
        leadBarristerId: assignedLeadId,
        ...(needsOverride && overrideReady ? { conflictOverrideReason: overrideReason.trim() } : {}),
        partyA: partyA.name.trim()
          ? {
              contactId: partyA.contactId,
              name: partyA.name.trim(),
              role: partyARole,
              ...(partyAPhone.trim() ? { phone: partyAPhone.trim() } : {}),
              ...(partyAAltPhone.trim() ? { altPhone: partyAAltPhone.trim() } : {}),
              ...(partyAEmail.trim() ? { email: partyAEmail.trim() } : {}),
            }
          : undefined,
        partyB: partyB.name.trim()
          ? {
              contactId: partyB.contactId,
              name: partyB.name.trim(),
              role: partyBRole,
              ...(partyBPhone.trim() ? { phone: partyBPhone.trim() } : {}),
              ...(partyBEmail.trim() ? { email: partyBEmail.trim() } : {}),
            }
          : undefined,
        instructingSolicitor:
          solicitor.contactId || solicitor.name.trim() || solicitorPhone.trim() || solicitorAltPhone.trim() || solicitorEmail.trim()
            ? {
                contactId: solicitor.contactId,
                // Only pass a name when creating a new solicitor (no contactId) — never overwrite a picked one.
                name: solicitor.contactId ? undefined : (solicitor.name.trim() || undefined),
                ...(solicitorPhone.trim() ? { phone: solicitorPhone.trim() } : {}),
                ...(solicitorAltPhone.trim() ? { altPhone: solicitorAltPhone.trim() } : {}),
                ...(solicitorEmail.trim() ? { email: solicitorEmail.trim() } : {}),
              }
            : undefined,
        onSuccess: async (realMatter) => {
          // Bust all matter-picker caches so modals (comms, tasks, fees, activities) show the new matter immediately
          void queryClient.invalidateQueries({ queryKey: ['matters'] });

          // Created straight from an inbox instruction → auto-link the email/conversation so the
          // barrister doesn't have to come back and link it manually (one fluid triage action).
          if (locationState?.linkConversationId) {
            try { await linkConversationToMatter(locationState.linkConversationId, realMatter.id); }
            catch { toast.warning('Matter created, but the email could not be linked automatically'); }
          } else if (locationState?.linkEmailId) {
            try { await linkEmailToMatter(locationState.linkEmailId, realMatter.id); }
            catch { toast.warning('Matter created, but the email could not be linked automatically'); }
          }

          if (isBarrister) {
            toast.success(`Matter "${realMatter.name}" created — ${realMatter.number}`);
          } else if (assignedBarrister) {
            toast.success(`Matter created and assigned to ${assignedBarrister.fullName}`);
          } else {
            toast.success('Matter created — offer the brief to a barrister using the Allocation card');
          }
          navigate(`${basePath}/matters/${realMatter.id}`);
        },
        onError: (err) => {
          setSubmitting(false);
          if (err.status === 409) {
            // Blocked by the server's conflict gate — show the override path instead of a dead end.
            setServerConflict(err.message);
            toast.error(err.message);
          } else {
            toast.error(`Could not create matter: ${err.message}`);
          }
        },
      },
    );
  };

  // ── Derived display values for the summary sidebar ──
  const selectedCourt = courts.find((c) => c.id === courtId);
  const selectedArea = practiceAreas.find((p) => p.id === practiceAreaId);
  const selectedCaseType = CASE_TYPES.find((t) => t.value === caseType);
  const selectedFeeType = FEE_TYPES.find((f) => f.value === feeType);
  const selectedPriority = PRIORITIES.find((p) => p.value === priority);

  // A PUPIL HOLDS NO `case.create`, so `POST /matters` answers 403 whatever this form collects.
  // The nav links and both "New matter" buttons are already withheld (MyMattersPage,
  // MatterPipelinePage, SetupContinuation), but all three `/*/matters/new` routes stayed reachable
  // by typing the URL or using browser history. The form then rendered and took every field; the
  // debounced conflict scan (`:220` → `POST /conflicts/scan`) answered 403 too, since
  // `CONFLICT_ROLES` also excludes a pupil; and submit refused last. Same answer as Intake and
  // Reports give: say which it is, using the shared EmptyState, and never open the form.
  //
  // Placed AFTER every hook so hook order is identical for every role. Server-side authorization is
  // unchanged and remains the only thing that actually decides this.
  if (!canCreateMatter(user?.memberType)) {
    return (
      <div>
        <PageHeader title="New Matter" onBack={() => navigate(-1)} />
        <EmptyState
          icon={<Briefcase className="h-6 w-6" />}
          title="Opening a matter isn't available to your role"
          description="Instructions are accepted by the tenants and clerks on the matter. You can read, and work on, every matter you are assigned to."
        />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="New Matter"
        onBack={() => navigate(-1)}
      />

      <div className="grid gap-6 lg:grid-cols-[1fr_300px]">
        {/* ── Left column ── */}
        <div className="space-y-5">

          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="h-4 w-4 text-brand-600 dark:text-brand-400" /> Basic Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* 3-up on a wide screen: the short fields below sat one pair per row and pushed
                  the form past two full screens for no reason. The matter name keeps the full
                  width because it is the longest value on the form. */}
              <div className={`grid gap-4 sm:grid-cols-2 ${basicCols}`}>
                {/* Full width whatever the column count — the longest value on the form. */}
                <div className="col-span-full">
                  <label className={labelCls}>Matter name <span className="text-red-500">*</span></label>
                  <input
                    className={`${inputCls} ${errFor('name') ? 'border-red-400 focus:ring-red-400/40' : ''}`}
                    value={name}
                    onChange={(e) => setName(sanitizeMatterName(e.target.value))}
                    onBlur={() => markTouched('name')}
                    maxLength={200}
                    placeholder="e.g. R v Smith"
                  />
                  {errFor('name') && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errFor('name')}</p>}
                </div>
                <div>
                  <label className={labelCls}>Practice area</label>
                  <select
                    className={selectCls}
                    value={practiceAreaId}
                    onChange={(e) => setPracticeAreaId(e.target.value)}
                    disabled={refLoading}
                  >
                    <option value="">{refLoading ? 'Loading…' : 'Select area'}</option>
                    {practiceAreas.map((a) => (
                      <option key={a.id} value={a.id}>{a.name}</option>
                    ))}
                  </select>
                </div>
                {moreOpen && (
                  <div>
                    <label className={labelCls}>Sub-area</label>
                    <input
                      className={inputCls}
                      value={subArea}
                      onChange={(e) => setSubArea(e.target.value)}
                      placeholder="e.g. Fraud, Ancillary relief"
                    />
                  </div>
                )}
                <div>
                  <label className={labelCls}>Case type <span className="text-red-600 dark:text-red-400">*</span></label>
                  <select
                    className={`${selectCls} ${errFor('caseType') ? 'border-red-400 focus:ring-red-400/40' : ''}`}
                    value={caseType}
                    onChange={(e) => setCaseType(e.target.value)}
                    onBlur={() => markTouched('caseType')}
                  >
                    <option value="">Select type</option>
                    {CASE_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                  {errFor('caseType') && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errFor('caseType')}</p>}
                </div>
                {moreOpen && (
                  <div>
                    <label className={labelCls}>Court case reference</label>
                    <input
                      className={`${inputCls} ${errFor('courtCaseNumber') ? 'border-red-400 focus:ring-red-400/40' : ''}`}
                      value={courtCaseNumber}
                      onChange={(e) => setCourtCaseNumber(e.target.value)}
                      onBlur={() => markTouched('courtCaseNumber')}
                      maxLength={COURT_REF_MAX}
                      placeholder="e.g. CL-2026-001234"
                    />
                    {errFor('courtCaseNumber') && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errFor('courtCaseNumber')}</p>}
                  </div>
                )}
                {/* Lead barrister — admins/clerks assign the matter directly here. A barrister
                    creating their own matter is auto-assigned, so this is hidden for them.
                    Optional: leave "Unassigned" to allocate later via the offer-brief flow. */}
                {!isBarrister && (
                  <div>
                    <label className={labelCls}>Lead barrister</label>
                    <select
                      className={selectCls}
                      value={leadBarristerId}
                      onChange={(e) => setLeadBarristerId(e.target.value)}
                    >
                      <option value="">Unassigned — allocate later</option>
                      {barristers.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.fullName}{b.user.status === 'invited' ? ' (invite pending)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* One control for every optional extra on the form — Sub-area, Court case
              reference and the alternate phone number. Hidden by default because a matter
              is usually opened in a hurry with none of them. */}
          {!moreOpen && (
            <button
              type="button"
              onClick={() => setShowMore(true)}
              className="-mt-2 self-start text-xs font-medium text-brand-600 hover:text-brand-700 dark:text-brand-400"
            >
              + Add more details (sub-area, court reference, alternate phone)
            </button>
          )}

          {/* Court & Judge */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Scales className="h-4 w-4 text-brand-600 dark:text-brand-400" /> Court &amp; Judge
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-3">
                <div>
                  <label className={labelCls}>Court</label>
                  <select
                    className={selectCls}
                    value={courtId}
                    onChange={(e) => setCourtId(e.target.value)}
                    disabled={refLoading}
                  >
                    <option value="">{refLoading ? 'Loading…' : 'Select court'}</option>
                    {courts.map((c) => (
                      <option key={c.id} value={c.id}>{c.shortName} — {c.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Courtroom</label>
                  <input
                    className={inputCls}
                    value={courtroom}
                    onChange={(e) => setCourtroom(e.target.value)}
                    placeholder="e.g. Court 4"
                  />
                </div>
                <div>
                  <label className={labelCls}>Judge</label>
                  <input
                    className={inputCls}
                    value={judge}
                    onChange={(e) => setJudge(e.target.value)}
                    placeholder="e.g. HHJ Richardson"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* People */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-4 w-4 text-brand-600 dark:text-brand-400" /> People
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isSolo ? (
                <div className="rounded-md border border-emerald-100 bg-emerald-50 dark:bg-emerald-500/10 px-3 py-2 text-xs text-emerald-800 dark:text-emerald-300 mb-4">
                  You will be automatically assigned as lead barrister. The matter opens as <strong>Active</strong> immediately.
                </div>
              ) : (
                <div className="rounded-md border border-blue-100 bg-blue-50 dark:bg-blue-500/10 px-3 py-2 text-xs text-blue-800 dark:text-blue-300 mb-4">
                  Barrister assignment is handled after creation via the <strong>Allocation</strong> card on the matter page — the formal offer/accept handshake is required for BSB cab-rank compliance.
                </div>
              )}
              <div className="sm:col-span-2">
                <ContactPicker
                  label="Instructing solicitor"
                  placeholder="Firm or individual name"
                  value={solicitor}
                  onChange={(c) => { setSolicitor(c); markTouched('solicitorName'); }}
                  filterType="solicitor"
                />
                {errFor('solicitorName') && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errFor('solicitorName')}</p>}
              </div>
              {/* Instructing solicitor contact details — primary + alternate number and email.
                  Saved onto the solicitor Contact (reused across future matters). All optional. */}
              {/* Two fields or three, depending on whether the alternate number is showing. A
                  fixed three columns left the third empty while it was hidden, so the email
                  stopped two thirds across and the phone had too little room for its number. */}
              <div
                className={`mt-3 grid gap-4 sm:col-span-2 ${
                  moreOpen ? 'sm:grid-cols-3' : 'sm:grid-cols-2'
                }`}
              >
                <div>
                  <label className={labelCls}>Primary number <span className="font-normal text-muted-foreground">(optional)</span></label>
                  <PhoneInput
                    value={solicitorPhone}
                    onChange={setSolicitorPhone}
                    onBlur={() => markTouched('solicitorPhone')}
                    error={errFor('solicitorPhone')}
                  />
                </div>
                {moreOpen && (
                  <div>
                    <label className={labelCls}>Alternate number <span className="font-normal text-muted-foreground">(optional)</span></label>
                    <PhoneInput
                      value={solicitorAltPhone}
                      onChange={setSolicitorAltPhone}
                      onBlur={() => markTouched('solicitorAltPhone')}
                      error={errFor('solicitorAltPhone')}
                    />
                  </div>
                )}
                <div>
                  <label className={labelCls}>Email <span className="font-normal text-muted-foreground">(optional)</span></label>
                  <input
                    type="email"
                    className={`${inputCls} ${errFor('solicitorEmail') ? 'border-red-400 focus:ring-red-400/40' : ''}`}
                    value={solicitorEmail}
                    onChange={(e) => setSolicitorEmail(e.target.value)}
                    onBlur={() => markTouched('solicitorEmail')}
                    maxLength={255}
                    placeholder="clerk@chambers.co.uk"
                  />
                  {errFor('solicitorEmail') && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errFor('solicitorEmail')}</p>}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Parties & Conflict Check */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Scales className="h-4 w-4 text-brand-600 dark:text-brand-400" /> Parties &amp; Conflict Check
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="mb-3 rounded-md border border-blue-100 bg-blue-50 dark:bg-blue-500/10 p-2.5 text-xs text-blue-900 dark:text-blue-200">
                Enter party names. As you type the system scans <strong>all existing matters</strong> in the database for conflicts in real time.
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <ContactPicker
                    label="Party A (your client's side)"
                    placeholder="e.g. Acme Ltd"
                    value={partyA}
                    onChange={setPartyA}
                  />
                  {errFor('partyAName') && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errFor('partyAName')}</p>}
                  <select
                    className={`mt-1.5 ${selectCls}`}
                    value={partyARole}
                    onChange={(e) => setPartyARole(e.target.value)}
                  >
                    <option value="claimant">Claimant</option>
                    <option value="defendant">Defendant</option>
                    <option value="applicant">Applicant</option>
                    <option value="respondent">Respondent</option>
                    <option value="prosecution">Prosecution</option>
                    <option value="defence">Defence</option>
                    <option value="petitioner">Petitioner</option>
                  </select>
                  <div className="mt-2">
                    <label className={labelCls}>Client phone <span className="text-red-500">*</span></label>
                    <PhoneInput
                      value={partyAPhone}
                      onChange={setPartyAPhone}
                      onBlur={() => markTouched('partyAPhone')}
                      error={errFor('partyAPhone')}
                    />
                  </div>
                  {moreOpen && (
                    <div className="mt-2">
                      <label className={labelCls}>Alt. phone <span className="font-normal text-muted-foreground">(optional)</span></label>
                      <PhoneInput
                        value={partyAAltPhone}
                        onChange={setPartyAAltPhone}
                        onBlur={() => markTouched('partyAAltPhone')}
                        error={errFor('partyAAltPhone')}
                      />
                    </div>
                  )}
                  <div className="mt-2">
                    <label className={labelCls}>Client email <span className="text-red-500">*</span></label>
                    <input
                      type="email"
                      className={`${inputCls} ${errFor('partyAEmail') ? 'border-red-400 focus:ring-red-400/40' : ''}`}
                      value={partyAEmail}
                      onChange={(e) => setPartyAEmail(e.target.value)}
                      onBlur={() => markTouched('partyAEmail')}
                      maxLength={255}
                      placeholder="contact@acmeltd.com"
                    />
                    {errFor('partyAEmail') && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errFor('partyAEmail')}</p>}
                  </div>
                </div>
                <div>
                  <ContactPicker
                    label="Party B (opposing side)"
                    placeholder="e.g. Beta Corp"
                    value={partyB}
                    onChange={(c) => { setPartyB(c); markTouched('partyBName'); }}
                  />
                  {errFor('partyBName') && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errFor('partyBName')}</p>}
                  <select
                    className={`mt-1.5 ${selectCls}`}
                    value={partyBRole}
                    onChange={(e) => setPartyBRole(e.target.value)}
                  >
                    <option value="defendant">Defendant</option>
                    <option value="claimant">Claimant</option>
                    <option value="respondent">Respondent</option>
                    <option value="applicant">Applicant</option>
                    <option value="defence">Defence</option>
                    <option value="prosecution">Prosecution</option>
                    <option value="beneficiary">Beneficiary</option>
                  </select>
                  <div className="mt-2">
                    <label className={labelCls}>Phone <span className="font-normal text-muted-foreground">(optional)</span></label>
                    <PhoneInput
                      value={partyBPhone}
                      onChange={setPartyBPhone}
                      onBlur={() => markTouched('partyBPhone')}
                      error={errFor('partyBPhone')}
                    />
                  </div>
                  {/* Saved onto the opposing party's Contact — the conflict engine matches
                      future matters on email as well as name and phone. */}
                  <div className="mt-2">
                    <label className={labelCls}>Email <span className="font-normal text-muted-foreground">(optional)</span></label>
                    <input
                      type="email"
                      className={`${inputCls} ${errFor('partyBEmail') ? 'border-red-400 focus:ring-red-400/40' : ''}`}
                      value={partyBEmail}
                      onChange={(e) => setPartyBEmail(e.target.value)}
                      onBlur={() => markTouched('partyBEmail')}
                      maxLength={255}
                      placeholder="contact@opposing.co.uk"
                    />
                    {errFor('partyBEmail') && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errFor('partyBEmail')}</p>}
                  </div>
                </div>
              </div>

              {/* Conflict scan results */}
              {scanning && (
                <div className="mt-4 flex items-center gap-2 rounded-md border border-blue-200 dark:border-blue-500/30 bg-blue-50 dark:bg-blue-500/10 p-3 text-xs text-blue-800 dark:text-blue-300">
                  <CircleNotch className="h-4 w-4 animate-spin text-blue-600 dark:text-blue-400" />
                  Scanning all matters in the database…
                </div>
              )}
              {!scanning && scanResults.length > 0 && (
                <div className="mt-4 space-y-2">
                  <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-foreground">
                    <Scales className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
                    {scanResults.length} match{scanResults.length > 1 ? 'es' : ''} found in existing matters
                  </div>
                  {scanResults.map((r, i) => (
                    <div
                      key={i}
                      className={`rounded-md border p-3 ${
                        r.severity === 'critical'
                          ? 'border-red-300 dark:border-red-500/40 bg-red-50 dark:bg-red-500/10'
                          : r.severity === 'warning'
                            ? 'border-amber-300 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-500/10'
                            : 'border-blue-200 dark:border-blue-500/30 bg-blue-50 dark:bg-blue-500/10'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <Badge variant={r.severity === 'critical' ? 'danger' : r.severity === 'warning' ? 'warning' : 'info'}>
                          {r.severity}
                        </Badge>
                        <span className="text-xs font-semibold text-foreground">
                          Matched via {r.matchMethod.replace(/_/g, ' ')}
                        </span>
                      </div>
                      <div className="mt-1 text-xs text-foreground">{r.reason}</div>
                      <div className="mt-1 text-[10px] text-muted-foreground">
                        Existing matter: {r.existingMatterName} ({r.existingMatterNumber})
                        {r.existingBarrister && <> · Barrister: {r.existingBarrister}</>}
                      </div>
                      {r.severity === 'critical' && (
                        <div className="mt-2 rounded border border-red-200 dark:border-red-500/30 bg-card p-2 text-[10px] font-semibold text-red-800 dark:text-red-300">
                          CRITICAL: Review with Head of Chambers before proceeding.
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {!scanning && (partyA.name.trim() || partyB.name.trim()) && scanResults.length === 0 && (
                <div className="mt-4 flex items-center gap-2 rounded-md border border-emerald-200 dark:border-emerald-500/30 bg-emerald-50 dark:bg-emerald-500/10 p-3 text-xs text-emerald-800 dark:text-emerald-300">
                  <Scales className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                  <strong>Clear.</strong> No conflicts found — scanned all matters in the database.
                </div>
              )}
            </CardContent>
          </Card>

          {/* Financial */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CurrencyGbp className="h-4 w-4 text-brand-600 dark:text-brand-400" /> Financial
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-3">
                <div>
                  <label className={labelCls}>Fee type</label>
                  <select
                    className={selectCls}
                    value={feeType}
                    onChange={(e) => setFeeType(e.target.value)}
                  >
                    <option value="">Select fee type</option>
                    {FEE_TYPES.map((f) => (
                      <option key={f.value} value={f.value}>{f.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Fee estimate (&pound;)</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    className={`${inputCls} ${errFor('feeEstimate') ? 'border-red-400 focus:ring-red-400/40' : ''}`}
                    value={feeEstimate}
                    onChange={(e) => setFeeEstimate(sanitizeMoneyInput(e.target.value))}
                    onBlur={() => { setFeeEstimate(formatMoney2(feeEstimate)); markTouched('feeEstimate'); }}
                    placeholder="0.00"
                  />
                  {errFor('feeEstimate') && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errFor('feeEstimate')}</p>}
                </div>
                <div>
                  <label className={labelCls}>Priority</label>
                  <select
                    className={selectCls}
                    value={priority}
                    onChange={(e) => setPriority(e.target.value)}
                  >
                    {PRIORITIES.map((p) => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Description — collapsed until asked for. It is the second tallest block on the
              form and is routinely left empty when a matter is opened straight off a phone
              call; `descOpen` forces it back open the moment it holds text or an error. */}
          <Card>
            <CardHeader>
              <button
                type="button"
                onClick={() => setShowDescription(!descOpen)}
                aria-expanded={descOpen}
                className="flex w-full items-center justify-between gap-2 text-left"
              >
                <CardTitle className="flex items-center gap-2">
                  <NoteBlank className="h-4 w-4 text-brand-600 dark:text-brand-400" /> Description
                  {!descOpen && (
                    <span className="text-xs font-normal text-muted-foreground">(optional)</span>
                  )}
                </CardTitle>
                <span className="text-xs font-medium text-brand-600 dark:text-brand-400">
                  {descOpen ? 'Hide' : 'Add'}
                </span>
              </button>
            </CardHeader>
            {descOpen && (
            <CardContent>
              {/* Side by side on a wide screen — stacked, these two textareas were the second
                  tallest block on the form for no reason other than their order. */}
              <div className="grid gap-4 lg:grid-cols-2">
                <div>
                  <label className={labelCls}>Matter summary</label>
                  <ExpandableTextarea
                    label="Matter summary"
                    rows={3}
                    value={summary}
                    onChange={setSummary}
                    maxLength={TEXT_MAX}
                    placeholder="Brief description of the matter…"
                  />
                  {errFor('summary') && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errFor('summary')}</p>}
                </div>
                <div>
                  <label className={labelCls}>Internal notes</label>
                  <ExpandableTextarea
                    label="Internal notes"
                    rows={3}
                    value={notes}
                    onChange={setNotes}
                    maxLength={TEXT_MAX}
                    placeholder="Notes visible only to chambers staff…"
                  />
                  {errFor('notes') && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errFor('notes')}</p>}
                </div>
              </div>
            </CardContent>
            )}
          </Card>
        </div>

        {/* ── Right panel — sticky matter preview ── */}
        <div className="space-y-3 lg:sticky lg:top-20 lg:self-start">

          {/* Matter preview card */}
          <Card className="overflow-hidden">
            {/* Branded header */}
            <div className="bg-brand-600 px-4 py-4">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/15">
                  <Briefcase className="h-4 w-4 text-white" weight="duotone" />
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] font-semibold uppercase tracking-widest text-brand-200">
                    Matter Preview
                  </div>
                  <div className="truncate text-sm font-semibold text-white">
                    {name || <span className="italic text-brand-300 font-normal">Enter matter name…</span>}
                  </div>
                </div>
              </div>
            </div>

            {/* Key fields grid */}
            <CardContent className="p-4">
              <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                {[
                  ['Area',        selectedArea?.name ?? '—'],
                  ['Type',        selectedCaseType?.label ?? '—'],
                  ['Court',       selectedCourt?.shortName ?? '—'],
                  ['Fee type',    selectedFeeType?.label ?? '—'],
                  ['Fee est.',    feeEstimate ? `£${Number(feeEstimate).toFixed(2)}` : '—'],
                  ['Priority',    selectedPriority?.label ?? '—'],
                ].map(([label, val]) => (
                  <div key={label}>
                    <div className="mb-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{label}</div>
                    <div className="truncate text-sm font-medium text-foreground">{val}</div>
                  </div>
                ))}
              </div>

              {/* Divider */}
              <div className="my-4 h-px bg-border" />

              {/* Status row */}
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Opens as</span>
                {isSolo
                  ? <Badge variant="success">Active</Badge>
                  : <Badge variant="info">Intake</Badge>}
              </div>
              <p className="mt-1.5 text-xs text-muted-foreground">
                {isSolo
                  ? 'You are assigned as lead barrister. Schedule a hearing or upload documents immediately.'
                  : 'Offer the brief to a barrister after creation to activate the matter.'}
              </p>
            </CardContent>
          </Card>

          {/* Validation errors — all shown at once */}
          {validationErrors.length > 0 && (
            <div className="rounded-lg border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 p-3">
              <div className="flex items-center gap-2 mb-2">
                <WarningCircle className="h-4 w-4 shrink-0 text-red-600 dark:text-red-400" weight="fill" />
                <span className="text-xs font-semibold text-red-800 dark:text-red-300">
                  {validationErrors.length === 1
                    ? 'Please complete one more detail before continuing:'
                    : 'Please complete the following before continuing:'}
                </span>
              </div>
              <ul className="space-y-1">
                {validationErrors.map((e, i) => (
                  <li key={i} className="text-xs text-red-700 dark:text-red-300">• {e}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Conflict block */}
          {needsOverride && (
            <div data-testid="conflict-override" className="rounded-lg border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 p-3 text-sm text-red-800 dark:text-red-300">
              <div className="flex items-start gap-2">
                <WarningCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600 dark:text-red-400" weight="fill" />
                <div>
                  <div className="font-semibold">Critical conflict</div>
                  <div className="mt-0.5 text-xs">
                    {serverConflict ?? 'A critical conflict was detected with an existing matter.'}
                  </div>
                </div>
              </div>
              {canOverrideConflict ? (
                <div className="mt-3 space-y-1.5">
                  <label htmlFor="conflict-override-reason" className="block text-xs font-semibold">
                    Override reason (required to proceed)
                  </label>
                  <textarea
                    id="conflict-override-reason"
                    rows={3}
                    maxLength={1000}
                    value={overrideReason}
                    onChange={(e) => setOverrideReason(e.target.value)}
                    placeholder="Why it is proper to act despite this conflict — e.g. informed consent obtained from both clients."
                    className="w-full rounded-md border border-red-200 bg-card p-2 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-red-200 dark:border-red-500/30"
                  />
                  <p className="text-[11px]">
                    Recorded on the audit trail; the matter is marked "approved with override".
                    {overrideReason.trim().length > 0 && overrideReason.trim().length < 10 && ' At least 10 characters.'}
                  </p>
                </div>
              ) : (
                <p className="mt-2 text-xs">
                  Only a silk (KC) or the chambers admin can override a critical conflict. Ask one of them to create this matter.
                </p>
              )}
            </div>
          )}

          {/* Create button */}
          <Button
            variant={needsOverride ? 'destructive' : 'primary'}
            size="md"
            className="w-full"
            leftIcon={submitting ? <CircleNotch className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            disabled={!canCreate}
            onClick={handleCreate}
          >
            {submitting ? 'Creating…' : needsOverride ? (overrideReady ? 'Create with override' : 'Blocked — conflict') : 'Create matter'}
          </Button>
          <p className="text-center text-xs text-muted-foreground">
            {isSolo
              ? 'Takes you straight to the matter page.'
              : 'You can offer the brief immediately after.'}
          </p>
        </div>
      </div>
    </div>
  );
}
